/**
 * useViewerPanning Hook
 * Handles canvas panning in viewer mode (read-only)
 * Simplified version without fixed bounds - allows free panning
 */

import { useEffect, useRef, RefObject } from 'react'
import { dia } from '@joint/core'

export function useViewerPanning(
  paper: dia.Paper | null,
  canvasRef: RefObject<HTMLDivElement>
) {
  const isPanning = useRef(false)
  const lastMousePosition = useRef({ x: 0, y: 0 })

  useEffect(() => {
    if (!paper) return

    const handlePointerDown = (evt: dia.Event) => {
      isPanning.current = true
      lastMousePosition.current = { x: evt.clientX || 0, y: evt.clientY || 0 }
      if (canvasRef.current) {
        canvasRef.current.style.cursor = 'grabbing'
      }
    }

    // cell:pointerdown 의 콜백 시그니처는 (cellView, evt, x, y) — evt 만 꺼내 재사용.
    // 뷰어는 읽기전용(elementMove: false)이라 객체를 눌러도 이동/선택되지 않으므로,
    // 빈 공간뿐 아니라 객체(주차면 등)를 눌러도 팬이 시작되게 해야 밀집된 맵에서도
    // 자유롭게 드래그로 이동할 수 있다.
    const handleCellPointerDown = (_cellView: dia.CellView, evt: dia.Event) => {
      handlePointerDown(evt)
    }

    const handleMouseMove = (e: MouseEvent) => {
      if (!isPanning.current || !paper) return

      const dx = e.clientX - lastMousePosition.current.x
      const dy = e.clientY - lastMousePosition.current.y

      const currentTranslate = paper.translate()
      const newTx = currentTranslate.tx + dx
      const newTy = currentTranslate.ty + dy

      paper.translate(newTx, newTy)

      lastMousePosition.current = { x: e.clientX, y: e.clientY }
    }

    const handleMouseUp = () => {
      isPanning.current = false
      if (canvasRef.current) {
        canvasRef.current.style.cursor = 'grab'
      }
    }

    paper.on('blank:pointerdown', handlePointerDown)
    paper.on('cell:pointerdown', handleCellPointerDown)
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      paper.off('blank:pointerdown', handlePointerDown)
      paper.off('cell:pointerdown', handleCellPointerDown)
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [paper, canvasRef])
}
